/**
 * SCR-01 boot: the HTML splash (Pip popping out of the bag) stays up while the font, the
 * first-load atlases, the level pack and the first level load. Then Flow A goes straight
 * into level 1 (no menus) and Flow D opens the street map.
 */
import * as Phaser from 'phaser';
import { apiPlatform } from '@mp/adapters/platform';
import { app } from '../game/context.js';
import { startCity } from './MapScene.js';
import { handleLink, openView } from '../features/registry.js';
import { t } from '../i18n/index.js';
import { bootRoute, currentStep } from '../services/firstSession.js';
import { loadLook } from '../views/pip-looks.js';

declare global {
  interface Window {
    __mpSlowTimer?: ReturnType<typeof setTimeout>;
    __mpBootFailed?: () => void;
  }
}

function splashProgress(p: number): void {
  const bar = document.getElementById('splash-bar');
  if (bar) bar.style.width = `${Math.round(12 + 88 * Math.min(1, p))}%`;
}

export function hideSplash(): void {
  const el = document.getElementById('splash');
  if (window.__mpSlowTimer) clearTimeout(window.__mpSlowTimer);
  if (!el) return;
  el.classList.add('done');
  setTimeout(() => el.remove(), 400);
}

/** Localise the static splash copy once i18n is available. */
export function localiseSplash(): void {
  const set = (sel: string, key: string) => {
    const el = document.querySelector(sel);
    if (el) el.textContent = t(key);
  };
  set('#splash .slow-msg', 'splash.slow');
  set('#splash .fail-msg', 'splash.failed');
  set('#splash-retry', 'splash.retry');
}

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([p, new Promise<T>((r) => setTimeout(() => r(fallback), ms))]);
}

export class BootScene extends Phaser.Scene {
  constructor() {
    super('boot');
  }

  create(): void {
    void this.run();
  }

  private async run(): Promise<void> {
    const a = app();
    const s = a.services;
    localiseSplash();
    splashProgress(0.05);
    // dev/e2e only: ?level=N jumps straight into a level (mechanics QA)
    const forced = a.flags.dev || a.flags.e2e ? Number(new URLSearchParams(location.search).get('level') ?? 0) : 0;
    const route = forced > 0 ? ({ to: 'play', level: forced } as const) : bootRoute(s.store.state);
    // MT-01 journey: the map opens in the city the player was last in, while they can still be
    // there; a round straight from boot (the first session, ?level=N) is the home city's
    a.city = route.to === 'play' ? s.store.state.city : startCity(a, s.store.state.lastCity);
    const fontReady = withTimeout(
      document.fonts.load(`700 20px "Fredoka Variable"`).then(() => true),
      1500,
      false,
    );
    await a.art.loadIndex('generated/atlas/index.json');
    await Promise.all([a.art.loadFirst((p) => splashProgress(0.1 + p * 0.6)), s.levels.loadPack(a.city), fontReady]);
    splashProgress(0.8);
    a.art.createPipAnims(this.anims);
    // the first round needs its customer; everything else streams in behind it
    if (route.to === 'play') await Promise.all([s.levels.load(a.city, route.level), a.art.ensureGroup(`customers_${a.city}`)]);
    splashProgress(1);
    s.analytics.track('session_start', {
      step: currentStep(s.store.state),
      returning: s.store.state.createdAt < s.now() - 60_000,
      levels: s.levels.usingDevLevels(a.city) ? 'dev' : 'pack',
      art: a.art.has('ui_play_slot') ? 'atlas' : 'fallback',
      platform: apiPlatform(s.providers.platform),
    });
    if (route.to === 'play' && route.level === 1) s.analytics.track('tutorial_step', { step: 'splash_done' });
    // SY-02: in the EU/UK the law wants the age check and consent before anything is tracked
    // (shown over the splash; the overlay layer sits above it)
    if (s.privacy.askAtBoot) {
      if (window.__mpSlowTimer) clearTimeout(window.__mpSlowTimer);
      document.getElementById('splash')?.classList.remove('slow');
      await openView(a, 'privacyGate', { reason: 'boot' });
    }
    hideSplash();
    // dev/e2e `?open=<viewKind>`: screenshot a feature screen straight after boot (returning players)
    const devOpen = a.session.devOpen;
    a.session.devOpen = null;
    if (devOpen && route.to === 'play') console.warn('[market-pop] ?open= needs a player past the first session (seed one)');
    if (route.to === 'play') this.scene.start('play', { level: route.level });
    else this.scene.start('map', { reveal: route.reveal, ...(devOpen ? { open: devOpen.kind, openProps: devOpen.props, quiet: true } : {}) });
    // warm the rest of the city's art in the background, then add the extra Pip actions
    void a.art.loadRest(a.city).then(() => a.art.createPipAnims(this.anims));
    // and draw the look Pip wears (views/pip-looks.ts), so he has it when he first appears
    void loadLook(this.game, s.store.state.skin);
    // SO-05: a friend invite / magic link / transfer that opened the game
    const link = a.session.deepLink;
    a.session.deepLink = null;
    if (link) void handleLink(a, link);
  }
}
